import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/ui/avatar'
import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { CardContent } from '@repo/ui/components/ui/card'
import {
  CalendarDays,
  CalendarPlus,
  Check,
  Clock,
  ExternalLink,
  type LucideIcon,
  MapPin,
  Phone,
  Plus,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react'
import type React from 'react'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import { PremiumPanel } from '@/components/ui/premium-panel'
import {
  type DisplayGroupBookingPageConfig,
  formatBookingPrice,
  normalizeServiceBookingConfig,
  type OperatorBookingPageTheme,
  resolveServicePriceDisplay,
  type ServiceBookingPageConfig,
} from '@/lib/booking-page-customization'
import { formatPhoneDisplay } from '@/lib/format-phone'

export type BookingStorefrontService = {
  id: string
  name: string
  slug: string
  displayGroupId: string | null
  description: string | null
  durationMinutes: number
  price: number | null
  allowQuantity: boolean
  quantityLabel: string | null
  maxQuantity: number | null
  bookingPageConfig?: ServiceBookingPageConfig | null
}

export type BookingStorefrontOperator = {
  businessName: string
  slug: string
  bio: string | null
  phone: string | null
  logoUrl: string | null
}

export type BookingStorefrontResult = {
  id: string
  date: string
  startTime: string
  endTime: string
}

export function StorefrontHero({
  operator,
  theme,
  services,
}: {
  operator: BookingStorefrontOperator
  theme: OperatorBookingPageTheme
  services: BookingStorefrontService[]
}) {
  const headline = theme.hero.headline || `Book ${operator.businessName}`
  const subheadline = theme.hero.subheadline || operator.bio || ''
  const backgroundImage = theme.hero.backgroundImageUrl || theme.hero.bannerImageUrl
  const heroSteps =
    theme.flowStyle === 'address_first'
      ? ['Add location', 'Choose service', 'Select time']
      : ['Choose service', 'Add location', 'Select time']

  return (
    <section
      className={`relative w-full min-w-0 overflow-hidden border border-border/70 bg-card ${getSurfaceHeroClass(theme.surfaceStyle)} ${
        theme.pageLayout === 'split' ? 'min-h-[28rem]' : 'min-h-[22rem]'
      }`}
    >
      {backgroundImage ? (
        <img src={backgroundImage} alt="" className="absolute inset-0 size-full object-cover" />
      ) : null}
      <div className={`absolute inset-0 ${getHeroOverlayClass(theme.hero.overlayStyle)}`} />
      <div
        className={`relative grid min-h-[inherit] min-w-0 gap-5 ${getSurfaceHeroPaddingClass(
          theme.surfaceStyle
        )} md:grid-cols-[minmax(0,1fr)_18rem]`}
      >
        <div className="flex min-w-0 max-w-3xl flex-col justify-end">
          <div
            className={`mb-4 inline-flex w-fit max-w-full items-center gap-2 border border-white/25 bg-zinc-950/25 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-sm backdrop-blur ${getSurfacePillClass(
              theme.surfaceStyle
            )}`}
          >
            <CalendarDays className="size-3.5" />
            Online booking
          </div>
          <h1 className="max-w-full text-balance break-words text-4xl font-semibold leading-tight text-white md:text-6xl">
            {headline}
          </h1>
          {subheadline ? (
            <p className="mt-4 max-w-2xl break-words text-base leading-7 text-white/88 md:text-lg">
              {subheadline}
            </p>
          ) : null}
        </div>
        <div
          className={`min-w-0 self-end border border-white/20 bg-white/88 p-4 text-foreground shadow-sm backdrop-blur ${getSurfaceInsetClass(
            theme.surfaceStyle
          )}`}
        >
          <div className="flex items-center gap-3">
            <Avatar
              className={`size-12 border border-border/70 bg-muted ${getSurfaceAvatarClass(
                theme.surfaceStyle
              )}`}
            >
              <AvatarImage src={operator.logoUrl ?? undefined} alt={operator.businessName} />
              <AvatarFallback
                className={`${getSurfaceAvatarClass(theme.surfaceStyle)} font-semibold`}
              >
                {getBusinessInitials(operator.businessName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="line-clamp-2 break-words font-semibold leading-snug">
                {operator.businessName}
              </p>
              <p className="text-xs text-muted-foreground">
                {services.length} service{services.length === 1 ? '' : 's'} available
              </p>
            </div>
          </div>
          <div className="mt-4 grid gap-2 text-xs text-muted-foreground">
            {heroSteps.map((label, index) => (
              <div key={label} className="flex items-center gap-2">
                <span
                  className={`grid size-5 place-items-center bg-primary/10 font-semibold text-primary ${getSurfacePillClass(
                    theme.surfaceStyle
                  )}`}
                >
                  {index + 1}
                </span>
                <span>{label}</span>
              </div>
            ))}
          </div>
          {theme.hero.bannerImageUrl && theme.hero.bannerImageUrl !== backgroundImage ? (
            <img
              src={theme.hero.bannerImageUrl}
              alt=""
              className={`mt-4 aspect-[16/7] w-full object-cover ${getSurfaceMediaClass(
                theme.surfaceStyle
              )}`}
            />
          ) : null}
        </div>
      </div>
    </section>
  )
}

export function ServiceSelectionStage({
  services,
  selectedServices,
  theme,
  displayGroupById,
  onToggleService,
  renderActions,
}: {
  services: BookingStorefrontService[]
  selectedServices: BookingStorefrontService[]
  theme: OperatorBookingPageTheme
  displayGroupById: Map<string, DisplayGroupBookingPageConfig>
  onToggleService: (service: BookingStorefrontService) => void
  renderActions: () => React.ReactNode
}) {
  if (theme.pageLayout === 'showcase') {
    return (
      <StorefrontServiceStage
        services={services}
        selectedServices={selectedServices}
        theme={theme}
        displayGroupById={displayGroupById}
        onToggleService={onToggleService}
        renderActions={renderActions}
      />
    )
  }

  if (theme.pageLayout === 'split') {
    return (
      <EditorialServiceStage
        services={services}
        selectedServices={selectedServices}
        theme={theme}
        displayGroupById={displayGroupById}
        onToggleService={onToggleService}
        renderActions={renderActions}
      />
    )
  }

  return (
    <PremiumPanel
      className={`flex flex-1 overflow-hidden ${getSurfacePanelClass(theme.surfaceStyle)}`}
    >
      <CardContent className={`flex flex-1 flex-col ${getSurfaceContentClass(theme.surfaceStyle)}`}>
        <FlowHeading title={theme.bookingCopy.serviceMenuTitle} />
        <ServiceCardGrid
          services={services}
          selectedServices={selectedServices}
          theme={theme}
          displayGroupById={displayGroupById}
          onToggleService={onToggleService}
        />
        {renderActions()}
      </CardContent>
    </PremiumPanel>
  )
}

export function BookingCompletion({
  theme,
  operator,
  services,
  bookingResult,
  confirmationContent,
  selectedAddress,
  customerPhone,
  customerEmail,
  onRebook,
}: {
  theme: OperatorBookingPageTheme
  operator: BookingStorefrontOperator
  services: BookingStorefrontService[]
  bookingResult: BookingStorefrontResult
  confirmationContent: { headline: string; body: string }
  selectedAddress: string | null
  customerPhone: string
  customerEmail: string
  onRebook: () => void
}) {
  const prepItems = getSelectedPrepChecklist(services)
  const fallbackBody = `Your appointment is set for ${formatDateLabel(bookingResult.date)} at ${
    bookingResult.startTime
  }.`
  const ctas = [theme.completion.primaryCta, theme.completion.secondaryCta].filter(
    (cta, index, list) => cta !== 'none' && list.indexOf(cta) === index
  )

  return (
    <PremiumPanel
      className={`flex flex-1 overflow-hidden ${getSurfacePanelClass(theme.surfaceStyle)}`}
    >
      <CardContent
        className={`flex flex-1 flex-col ${getSurfaceCompletionContentClass(theme.surfaceStyle)}`}
      >
        <FlowHeading
          kicker="Confirmed"
          title={confirmationContent.headline}
          description={<MarkdownPreview markdown={confirmationContent.body || fallbackBody} />}
        />
        <div
          className={`overflow-hidden border border-border/70 ${getSurfaceInsetClass(theme.surfaceStyle)} ${
            theme.completion.layout === 'branded' ? 'bg-primary/7' : 'bg-card/70'
          }`}
        >
          <div className="border-b border-border/70 bg-background/72 p-5">
            <div
              className={`grid size-12 place-items-center bg-primary text-primary-foreground ${getSurfacePillClass(
                theme.surfaceStyle
              )}`}
            >
              <Check className="size-6" />
            </div>
            <p className="mt-4 text-lg font-semibold">
              {services.map((item) => item.name).join(' + ')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {formatDateLabel(bookingResult.date)} from {bookingResult.startTime} to{' '}
              {bookingResult.endTime}
            </p>
          </div>
          <div className="grid gap-3 p-5 md:grid-cols-2">
            <ConfirmationDetail icon={MapPin} label="Service address">
              {selectedAddress ?? 'Address saved with booking'}
            </ConfirmationDetail>
            {theme.completion.showContactCard ? (
              <ConfirmationDetail icon={Phone} label="Contact">
                {customerPhone ? formatPhoneDisplay(customerPhone) : 'Phone verified'}
                {customerEmail ? ` | ${customerEmail}` : ''}
              </ConfirmationDetail>
            ) : null}
            <ConfirmationDetail icon={CalendarPlus} label="Add to calendar">
              Calendar invite support is coming soon. Keep the confirmation message for now.
            </ConfirmationDetail>
            <ConfirmationDetail icon={ShieldCheck} label="Booking reference">
              {bookingResult.id}
            </ConfirmationDetail>
          </div>
          {theme.completion.showPrepChecklist && prepItems.length > 0 ? (
            <div className="border-t border-border/70 p-5">
              <p className="mb-3 text-sm font-semibold">Before we arrive</p>
              <ul className="grid gap-2 text-sm text-muted-foreground">
                {prepItems.map((item) => (
                  <li key={item} className="flex gap-2">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {ctas.length > 0 || theme.completion.showRebookCta ? (
            <div className="flex flex-wrap gap-2 border-t border-border/70 p-5">
              {ctas.map((cta) => (
                <CompletionCta key={cta} cta={cta} operator={operator} onRebook={onRebook} />
              ))}
              {theme.completion.showRebookCta && !ctas.includes('rebook') ? (
                <CompletionCta cta="rebook" operator={operator} onRebook={onRebook} />
              ) : null}
            </div>
          ) : null}
        </div>
      </CardContent>
    </PremiumPanel>
  )
}

function StorefrontServiceStage({
  services,
  selectedServices,
  theme,
  displayGroupById,
  onToggleService,
  renderActions,
}: {
  services: BookingStorefrontService[]
  selectedServices: BookingStorefrontService[]
  theme: OperatorBookingPageTheme
  displayGroupById: Map<string, DisplayGroupBookingPageConfig>
  onToggleService: (service: BookingStorefrontService) => void
  renderActions: () => React.ReactNode
}) {
  const featuredService =
    services.find(
      (service) => normalizeServiceBookingConfig(service.bookingPageConfig).featuredLabel
    ) ??
    services[0] ??
    null
  const featuredConfig = featuredService
    ? normalizeServiceBookingConfig(featuredService.bookingPageConfig)
    : null

  return (
    <PremiumPanel
      className={`flex flex-1 overflow-hidden ${getSurfacePanelClass(theme.surfaceStyle)}`}
    >
      <CardContent className={`flex flex-1 flex-col ${getSurfaceContentClass(theme.surfaceStyle)}`}>
        <FlowHeading title={theme.bookingCopy.serviceMenuTitle} />
        {featuredService && featuredConfig?.imageUrl ? (
          <FeaturedServiceCard
            service={featuredService}
            config={featuredConfig}
            theme={theme}
            selected={selectedServices.some((service) => service.id === featuredService.id)}
            onToggle={() => onToggleService(featuredService)}
          />
        ) : null}
        <ServiceCardGrid
          services={services}
          selectedServices={selectedServices}
          theme={theme}
          displayGroupById={displayGroupById}
          onToggleService={onToggleService}
          className="md:grid-cols-2 lg:grid-cols-3"
        />
        {renderActions()}
      </CardContent>
    </PremiumPanel>
  )
}

function EditorialServiceStage({
  services,
  selectedServices,
  theme,
  displayGroupById,
  onToggleService,
  renderActions,
}: {
  services: BookingStorefrontService[]
  selectedServices: BookingStorefrontService[]
  theme: OperatorBookingPageTheme
  displayGroupById: Map<string, DisplayGroupBookingPageConfig>
  onToggleService: (service: BookingStorefrontService) => void
  renderActions: () => React.ReactNode
}) {
  return (
    <PremiumPanel
      className={`flex flex-1 overflow-hidden ${getSurfacePanelClass(theme.surfaceStyle)}`}
    >
      <CardContent className={`flex flex-1 flex-col ${getSurfaceContentClass(theme.surfaceStyle)}`}>
        <div className="grid gap-5 lg:grid-cols-[minmax(15rem,0.72fr)_minmax(0,1fr)] lg:items-start">
          <div
            className={`border border-border/70 bg-background/68 p-4 ${getSurfaceInsetClass(theme.surfaceStyle)}`}
          >
            <FlowHeading title={theme.bookingCopy.serviceMenuTitle || 'Choose what to book'} />
            <div className="mt-5 grid gap-3">
              {services.slice(0, 3).map((service) => {
                const config = normalizeServiceBookingConfig(service.bookingPageConfig)
                return (
                  <div
                    key={service.id}
                    className={`overflow-hidden border border-border/60 bg-card/70 ${getSurfaceInsetClass(
                      theme.surfaceStyle
                    )}`}
                  >
                    {config.imageUrl ? (
                      <img
                        src={config.imageUrl}
                        alt=""
                        className="aspect-[5/3] w-full object-cover"
                      />
                    ) : null}
                    <div className="p-3">
                      <p className="text-sm font-semibold">{service.name}</p>
                      {service.description ? (
                        <p className="mt-1 line-clamp-2 text-xs leading-5 text-muted-foreground">
                          {service.description}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <ServiceCardGrid
            services={services}
            selectedServices={selectedServices}
            theme={theme}
            displayGroupById={displayGroupById}
            onToggleService={onToggleService}
            className="self-start"
          />
        </div>
        {renderActions()}
      </CardContent>
    </PremiumPanel>
  )
}

function FeaturedServiceCard({
  service,
  config,
  theme,
  selected,
  onToggle,
}: {
  service: BookingStorefrontService
  config: ServiceBookingPageConfig
  theme: OperatorBookingPageTheme
  selected: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={`mt-5 overflow-hidden border border-border/70 bg-card/70 ${getSurfaceInsetClass(
        theme.surfaceStyle
      )}`}
    >
      {config.imageUrl ? (
        <img src={config.imageUrl} alt="" className="aspect-[4/3] w-full object-cover" />
      ) : null}
      <div className="p-4">
        <div className="flex flex-wrap gap-2">
          <Badge>{config.featuredLabel || 'Featured'}</Badge>
          {config.badgeLabel ? <Badge variant="secondary">{config.badgeLabel}</Badge> : null}
        </div>
        <p className="mt-3 text-xl font-semibold">{service.name}</p>
        {service.description ? (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{service.description}</p>
        ) : null}
        <Button
          type="button"
          className="mt-4 w-full"
          variant={selected ? 'secondary' : 'default'}
          onClick={onToggle}
        >
          {selected ? 'Remove package' : 'Select package'}
        </Button>
      </div>
    </div>
  )
}

function ServiceCardGrid({
  services,
  selectedServices,
  theme,
  displayGroupById,
  onToggleService,
  className = '',
}: {
  services: BookingStorefrontService[]
  selectedServices: BookingStorefrontService[]
  theme: OperatorBookingPageTheme
  displayGroupById: Map<string, DisplayGroupBookingPageConfig>
  onToggleService: (service: BookingStorefrontService) => void
  className?: string
}) {
  if (services.length === 0) {
    return (
      <div className="rounded-[10px] border border-dashed border-border/70 bg-muted/20 p-5 text-sm text-muted-foreground">
        No services are available right now.
      </div>
    )
  }

  return (
    <div className={`grid gap-3 ${className}`}>
      {services.map((service) => {
        const selected = selectedServices.some((item) => item.id === service.id)
        const config = normalizeServiceBookingConfig(service.bookingPageConfig)
        const priceDisplay = resolveServicePriceDisplay(
          config,
          service.displayGroupId ? displayGroupById.get(service.displayGroupId) : null
        )

        return (
          <ServiceMerchandisingCard
            key={service.id}
            service={service}
            config={config}
            displayPrice={formatBookingPrice(service.price, priceDisplay)}
            selected={selected}
            theme={theme}
            onClick={() => onToggleService(service)}
          />
        )
      })}
    </div>
  )
}

function ServiceMerchandisingCard({
  service,
  config,
  displayPrice,
  selected,
  theme,
  onClick,
}: {
  service: BookingStorefrontService
  config: ServiceBookingPageConfig
  displayPrice: string | null
  selected: boolean
  theme: OperatorBookingPageTheme
  onClick: () => void
}) {
  const showMerchandising =
    theme.pageLayout !== 'standard' ||
    Boolean(
      config.imageUrl || config.featuredLabel || config.includedItems.length || config.upsellNote
    )
  const showCardMedia = showMerchandising && config.imageUrl && theme.serviceCardStyle !== 'compact'
  const includedItemLimit = theme.serviceCardStyle === 'compact' ? 2 : 4

  return (
    <button
      type="button"
      className={`group flex cursor-pointer gap-4 border text-left transition-all active:scale-[0.99] ${getServiceCardClass(
        theme.serviceCardStyle,
        showMerchandising
      )} ${getServiceCardToneClass(theme.surfaceStyle)} ${
        selected
          ? 'border-primary bg-primary/6'
          : 'border-border/70 bg-card/65 hover:border-primary/45 hover:bg-card'
      } ${showMerchandising ? 'flex-col' : 'items-center justify-between'}`}
      onClick={onClick}
    >
      {showCardMedia ? (
        <div
          className={`relative w-full overflow-hidden bg-muted ${getServiceCardMediaClass(
            theme.surfaceStyle,
            theme.serviceCardStyle
          )}`}
        >
          <img
            src={config.imageUrl}
            alt=""
            className="size-full object-cover transition-transform group-hover:scale-[1.02]"
          />
          {config.featuredLabel ? (
            <Badge className="absolute left-3 top-3 bg-background/90 text-foreground hover:bg-background/90">
              {config.featuredLabel}
            </Badge>
          ) : null}
        </div>
      ) : null}
      <div className="flex w-full items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3
              className={`${getServiceCardTitleClass(theme.serviceCardStyle)} ${
                theme.surfaceStyle === 'industrial' ? 'uppercase tracking-[0.08em]' : ''
              }`}
            >
              {service.name}
            </h3>
            {selected ? <Badge>Selected</Badge> : null}
            {config.badgeLabel ? <Badge variant="secondary">{config.badgeLabel}</Badge> : null}
            {!config.imageUrl && config.featuredLabel ? (
              <Badge variant="secondary">{config.featuredLabel}</Badge>
            ) : null}
          </div>
          {service.description ? (
            <p
              className={`mt-1 text-sm leading-6 text-muted-foreground ${
                theme.serviceCardStyle === 'compact'
                  ? 'line-clamp-1'
                  : theme.serviceCardStyle === 'editorial'
                    ? 'line-clamp-4'
                    : 'line-clamp-3'
              }`}
            >
              {service.description}
            </p>
          ) : null}
          {config.includedItems.length > 0 ? (
            <ul className="mt-3 grid gap-1.5 text-sm text-muted-foreground">
              {config.includedItems.slice(0, includedItemLimit).map((item) => (
                <li key={item} className="flex gap-2">
                  <Check className="mt-0.5 size-3.5 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {theme.showDuration ? (
              <span data-booking-duration className="inline-flex items-center gap-1">
                <Clock className="size-3.5" />
                {service.durationMinutes} min
              </span>
            ) : null}
            {displayPrice ? <Badge variant="secondary">{displayPrice}</Badge> : null}
          </div>
          {config.upsellNote ? (
            <p
              className={`mt-3 border border-primary/15 bg-primary/7 px-3 py-2 text-sm text-foreground ${getSurfaceSmallClass(
                theme.surfaceStyle
              )}`}
            >
              {config.upsellNote}
            </p>
          ) : null}
        </div>
        <div
          className={`grid size-10 shrink-0 place-items-center border transition-colors ${getSurfacePillClass(
            theme.surfaceStyle
          )} ${
            selected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-background group-hover:border-primary/50'
          }`}
        >
          {selected ? <Check className="size-4" /> : <Plus className="size-4" />}
        </div>
      </div>
    </button>
  )
}

function FlowHeading({
  kicker,
  title,
  description,
}: {
  kicker?: string
  title?: string
  description?: React.ReactNode
}) {
  if (!kicker && !title && !description) return null

  return (
    <div className="max-w-2xl">
      {kicker ? (
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
          {kicker}
        </p>
      ) : null}
      {title ? (
        <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">{title}</h2>
      ) : null}
      {description ? (
        <div className="mt-2 text-sm leading-6 text-muted-foreground">{description}</div>
      ) : null}
    </div>
  )
}

function ConfirmationDetail({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-[8px] border border-border/60 bg-background/65 p-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </div>
      <p className="mt-2 text-sm leading-6">{children}</p>
    </div>
  )
}

function CompletionCta({
  cta,
  operator,
  onRebook,
}: {
  cta: OperatorBookingPageTheme['completion']['primaryCta']
  operator: BookingStorefrontOperator
  onRebook: () => void
}) {
  if (cta === 'contact' && operator.phone) {
    return (
      <Button asChild type="button" variant="outline">
        <a href={`tel:${operator.phone}`}>
          <Phone className="size-4" />
          Contact {operator.businessName}
        </a>
      </Button>
    )
  }

  if (cta === 'rebook') {
    return (
      <Button type="button" variant="outline" onClick={onRebook}>
        <RotateCcw className="size-4" />
        Book another visit
      </Button>
    )
  }

  if (cta === 'manage') {
    return (
      <Button type="button" variant="outline" disabled>
        <ExternalLink className="size-4" />
        Manage link sent
      </Button>
    )
  }

  return null
}

function getHeroOverlayClass(overlayStyle: OperatorBookingPageTheme['hero']['overlayStyle']) {
  if (overlayStyle === 'none') return 'booking-hero-overlay-none'
  if (overlayStyle === 'strong') return 'booking-hero-overlay-strong'
  return 'booking-hero-overlay-soft'
}

function getSurfacePanelClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[3px] border-foreground/20 shadow-none'
  if (surfaceStyle === 'editorial') return 'rounded-[22px] shadow-sm'
  if (surfaceStyle === 'modern') return 'rounded-[12px] shadow-sm'
  return 'rounded-[10px] shadow-sm'
}

function getSurfaceContentClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'gap-4 p-4 md:p-5'
  if (surfaceStyle === 'editorial') return 'gap-7 p-5 md:p-8'
  return 'gap-4 p-4 md:p-5'
}

function getSurfaceCompletionContentClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'gap-5 p-4 md:p-5'
  if (surfaceStyle === 'editorial') return 'gap-7 p-6 md:p-8'
  return 'gap-6 p-5 md:p-7'
}

function getSurfaceHeroClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[4px] shadow-none'
  if (surfaceStyle === 'editorial') return 'rounded-[24px] shadow-sm'
  if (surfaceStyle === 'modern') return 'rounded-[14px] shadow-sm'
  return 'rounded-[12px] shadow-sm'
}

function getSurfaceHeroPaddingClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'p-4 md:p-6'
  if (surfaceStyle === 'editorial') return 'p-6 md:p-10'
  return 'p-5 md:p-8'
}

function getSurfaceInsetClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[18px]'
  if (surfaceStyle === 'modern') return 'rounded-[10px]'
  return 'rounded-[8px]'
}

function getSurfaceSmallClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[12px]'
  if (surfaceStyle === 'modern') return 'rounded-[8px]'
  return 'rounded-[6px]'
}

function getSurfacePillClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[3px]'
  if (surfaceStyle === 'editorial') return 'rounded-full'
  if (surfaceStyle === 'modern') return 'rounded-[10px]'
  return 'rounded-[999px]'
}

function getSurfaceAvatarClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[16px]'
  if (surfaceStyle === 'modern') return 'rounded-[10px]'
  return 'rounded-[8px]'
}

function getSurfaceMediaClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') return 'rounded-[2px]'
  if (surfaceStyle === 'editorial') return 'rounded-[14px]'
  if (surfaceStyle === 'modern') return 'rounded-[8px]'
  return 'rounded-[6px]'
}

function getServiceCardClass(
  serviceCardStyle: OperatorBookingPageTheme['serviceCardStyle'],
  showMerchandising = false
) {
  if (showMerchandising) {
    if (serviceCardStyle === 'compact') return 'min-h-20 p-3'
    if (serviceCardStyle === 'editorial') return 'p-5 md:p-6'
    return 'p-4 md:p-5'
  }
  if (serviceCardStyle === 'compact') return 'min-h-20 p-3'
  if (serviceCardStyle === 'editorial') return 'min-h-36 p-5 md:p-6'
  return 'min-h-28 p-4'
}

function getServiceCardMediaClass(
  surfaceStyle: OperatorBookingPageTheme['surfaceStyle'],
  serviceCardStyle: OperatorBookingPageTheme['serviceCardStyle']
) {
  const aspect = serviceCardStyle === 'editorial' ? 'aspect-[4/3]' : 'aspect-[16/9]'
  if (surfaceStyle === 'industrial') return `${aspect} rounded-[2px]`
  if (surfaceStyle === 'editorial') return `${aspect} rounded-[14px]`
  if (surfaceStyle === 'modern') return `${aspect} rounded-[8px]`
  return `${aspect} rounded-[6px]`
}

function getServiceCardTitleClass(serviceCardStyle: OperatorBookingPageTheme['serviceCardStyle']) {
  if (serviceCardStyle === 'compact') return 'text-base font-semibold'
  if (serviceCardStyle === 'editorial') return 'text-xl font-semibold'
  return 'text-lg font-semibold'
}

function getServiceCardToneClass(surfaceStyle: OperatorBookingPageTheme['surfaceStyle']) {
  if (surfaceStyle === 'industrial') {
    return 'rounded-[4px] shadow-none hover:bg-muted/35'
  }
  if (surfaceStyle === 'modern') {
    return 'rounded-[12px] shadow-sm'
  }
  if (surfaceStyle === 'editorial') {
    return 'rounded-[14px] shadow-sm'
  }
  return 'rounded-[10px] shadow-sm'
}

function getSelectedPrepChecklist(services: BookingStorefrontService[]) {
  return Array.from(
    new Set(
      services.flatMap(
        (service) => normalizeServiceBookingConfig(service.bookingPageConfig).prepChecklist
      )
    )
  )
}

function formatDateLabel(date: string) {
  const parsed = new Date(`${date}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) return date

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(parsed)
}

function getBusinessInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}
